/**
 * The small parts of the queue grammar: buttons, chips, pills and stamps.
 * Each one prints the class names `styles/queue.css` defines and nothing else,
 * so a surface can emit the same markup by hand when it has to.
 */
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "quiet";
  size?: "sm";
  /** A square glyph-only button; give it an aria-label. */
  icon?: boolean;
  /** The pressed state of a primary that toggles a panel. */
  on?: boolean;
}

export function Btn({ variant, size, icon, on, className, type = "button", ...rest }: BtnProps) {
  return (
    <button
      type={type}
      className={cx("btn", variant, size, icon && "icon", on && "on", className)}
      {...rest}
    />
  );
}

export interface ChipProps {
  children: ReactNode;
  /** A coloured dot before the text; `--agent` colours it. */
  dot?: boolean;
  /** `auto` greens the dot, `sup` blues it, `on` is a picked chip. */
  tone?: "auto" | "sup" | "on";
  /** The dot's colour, as a token (`var(--agent-dev)`). */
  agent?: string;
  onClick?: () => void;
  /** A page outside the app the chip opens in a new tab (the pull request on GitHub). */
  href?: string;
  title?: string;
  className?: string;
}

export function Chip({ children, dot, tone, agent, onClick, href, title, className }: ChipProps) {
  const cls = cx("chip", tone, className);
  const style = agent ? ({ "--agent": agent } as CSSProperties) : undefined;
  const body = (
    <>
      {dot && <span className="dot" aria-hidden />}
      {children}
    </>
  );
  if (href) {
    return (
      <a className={cls} style={style} href={href} target="_blank" rel="noreferrer" title={title}>
        {body}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cls} style={style} onClick={onClick} title={title}>
        {body}
      </button>
    );
  }
  return (
    <span className={cls} style={style} title={title}>
      {body}
    </span>
  );
}

export type PillKind = "needs" | "live" | "stop" | "done";

/** A state word: blue needs you, green moving, amber stopped, quiet done. */
export function Pill({ kind, children, dot = true }: { kind: PillKind; children: ReactNode; dot?: boolean }) {
  return (
    <span className={cx("pill", kind)}>
      {dot && <span className="dot" aria-hidden />}
      {children}
    </span>
  );
}

/** The mono stamp a settled record carries; it takes the colour it sits in. */
export function Stamp({ children, color }: { children: ReactNode; color?: string }) {
  return (
    <span className="stamp" style={color ? { color } : undefined}>
      {children}
    </span>
  );
}
